// Works out which go-live checklist items CentraOne can already confirm for a
// key, from what it has recorded: the key's request log (method, path and
// status only; never bodies) and the tenant's webhooks and their deliveries.
// Pure functions; the fetching lives in IntegrationGuide.tsx.

import type { ApiKeyRequestLogEntry } from '@services/api-keys.service';
import type { Webhook, WebhookDelivery } from '@services/integration.service';
import type { GoLiveCheckId } from './guideContent';

/** `unknown` = the data needed to tell could not be read. */
export type CheckState = 'done' | 'pending' | 'unknown';

export type CheckResults = Record<GoLiveCheckId, CheckState>;

export interface CheckInputs {
  /** The key's recent requests; null when the log could not be read. */
  requests: ApiKeyRequestLogEntry[] | null;
  webhooks: Webhook[] | null;
  deliveries: WebhookDelivery[] | null;
}

/** How many log pages the guide reads (50 requests each, newest first). */
export const CHECK_LOG_PAGES = 3;

type RequestSeen = Pick<ApiKeyRequestLogEntry, 'method' | 'path' | 'statusCode'>;

const TICKETS = '/api/tickets';
const STATUS_PATH = /^\/api\/tickets\/[^/]+\/status$/;

function normalisePath(path: string): string {
  const trimmed = path.replace(/\/+$/, '');
  return trimmed.startsWith('/api/') || trimmed === '/api' ? trimmed : `/api${trimmed}`;
}

function isSuccess(status: number): boolean {
  return status >= 200 && status < 300;
}

function seen(requests: RequestSeen[], match: (method: string, path: string, status: number) => boolean): boolean {
  return requests.some((request) =>
    match(request.method.toUpperCase(), normalisePath(request.path), request.statusCode),
  );
}

function fromBoolean(value: boolean | null): CheckState {
  if (value === null) return 'unknown';
  return value ? 'done' : 'pending';
}

export function evaluateChecks({ requests, webhooks, deliveries }: CheckInputs): CheckResults {
  const fromLog = (match: Parameters<typeof seen>[1]): CheckState =>
    fromBoolean(requests ? seen(requests, match) : null);

  return {
    authenticated: fromLog((_method, _path, status) => isSuccess(status)),
    ticket_created: fromLog(
      (method, path, status) => method === 'POST' && path === TICKETS && isSuccess(status),
    ),
    lookup: fromLog(
      (method, path, status) => method === 'GET' && path === `${TICKETS}/external` && isSuccess(status),
    ),
    duplicate: fromLog(
      (method, path, status) => method === 'POST' && path === TICKETS && status === 409,
    ),
    status_changed: fromLog(
      (method, path, status) => method === 'PATCH' && STATUS_PATH.test(path) && isSuccess(status),
    ),
    webhook_registered: fromBoolean(webhooks ? webhooks.some((webhook) => webhook.isEnabled) : null),
    webhook_delivered: fromBoolean(
      deliveries
        ? deliveries.some((delivery) => String(delivery.status).toLowerCase() === 'success')
        : null,
    ),
  };
}

export function countDone(results: CheckResults): number {
  return Object.values(results).filter((state) => state === 'done').length;
}
